/**
 * Utility functions for detecting the user's operating system
 */

type OperatingSystem = "Mac" | "Windows" | "Linux" | "iOS" | "Android" | "Unknown";

/**
 * Detects the user's operating system based on the user agent string
 * @returns The detected operating system
 */

const getOperatingSystem = (): OperatingSystem => {
    if (typeof window === "undefined") {
        return "Unknown";
    }
    const userAgent = window.navigator.userAgent;
    const platform = window.navigator.platform;

    switch (true) {
        // macOS / iOS
        case /Mac|iPhone|iPad|iPod/.test(platform) || /Mac OS X/.test(userAgent):
            return /iPhone|iPad|iPod/.test(userAgent) ? "iOS" : "Mac";

        // Windows
        case /Win/.test(platform) || /Windows/.test(userAgent):
            return "Windows";

        // Android
        case /Android/.test(userAgent):
            return "Android";

        // Linux (exclude Android)
        case /Linux/.test(platform) && !/Android/.test(userAgent):
            return "Linux";

        default:
            return "Unknown";
    }
};

/**
 * Checks if the user is on a Mac (macOS or iOS)
 * @returns true if the user is on a Mac
 */
export const isMac = (): boolean => {
    const os = getOperatingSystem();
    return os === "Mac" || os === "iOS";
};

/**
 * Checks if the user is on Windows
 * @returns true if the user is on Windows
 */
const isWindows = (): boolean => {
    return getOperatingSystem() === "Windows";
};

/**
 * Checks if the user is on a mobile device (iOS or Android)
 * @returns true if the user is on a mobile device
 */
const isMobile = (): boolean => {
    const os = getOperatingSystem();
    return os === "iOS" || os === "Android";
};
